import {Dispatch, SetStateAction} from 'react';
import {History} from 'history';
import {PagingParams, Sort} from '@pluto/table';

export interface MediaOrderListFilters {
    contentPartnerId: string;
    labPartner: string;
}

export interface MediaOrderValidationItem {
    cell: string;
    message: string;
    path: string;
    row: number;
}

export type SetMediaOrderIdField = (value: string) => void;
export type SetFilters = (filters: MediaOrderListFilters) => void;
export type SetValidations = (value: MediaOrderValidationItem[]) => void;
export type SetStateBoolean = Dispatch<SetStateAction<boolean>>;
export type OnModalTrigger = () => void;
export type SetImportModalOpen = () => void;
export type HandleFileSubmit = (files: File[]) => void;

export interface ImportMediaOrderActionParams {
    history: History;
    setMediaOrderId: SetMediaOrderIdField;
    listRowsPerPage: number;
    setValidations: SetValidations;
    setValidationsVisible: SetStateBoolean;
    handleClose: OnModalTrigger;
    setFilter: SetFilters;
    formData: FormData;
}

export type ImportMediaAction = (params: ImportMediaOrderActionParams) => void;

export type ImportErrorParams = Pick<
    ImportMediaOrderActionParams,
    'setValidations' | 'setValidationsVisible'
> & {
    errorData: {
        message: string;
        errors: MediaOrderValidationItem[];
    };
};

type ImportSuccessParamsPicks =
    | 'setFilter'
    | 'history'
    | 'handleClose'
    | 'listRowsPerPage'
    | 'setMediaOrderId';

export type ImportSuccessParams = Pick<
    ImportMediaOrderActionParams,
    ImportSuccessParamsPicks
>;

export interface HandleSubmitHandlers
    extends Omit<ImportMediaOrderActionParams, 'formData'> {
    importMedia: ImportMediaAction;
    setLoading: SetStateBoolean;
}

export type SetMediaOrderId = Dispatch<SetStateAction<string>>;

export interface MediaOrderImportContainerProps {
    setFilter: SetFilters;
    listRowsPerPage: number;
    setMediaOrderId: SetMediaOrderId;
    importMedia: ImportMediaAction;
}

export interface MediaOrderFilterParams extends PagingParams {
    contentPartnerId?: string;
    labPartner?: string;
    mediaOrderId?: string;
    importDateStart?: string;
    importDateEnd?: string;
    dateIssuedStart?: string;
    dateIssuedEnd?: string;
}

export interface MediaOrdersItemType {
    id: number;
    dueDateToLab: string;
    dueDateToPluto: string;
    mediaOrderId: string;
    availsId: string;
    seriesOrFilmTitle: string;
    seasonNumber: number;
    episodeNumber: number;
    episodeTitle: string | null;
    trt: string | null;
    clipRating: string;
    genre: string;
    subGenre: string;
    category: string;
    subCategory: string;
    territoriesInclude: string;
    territoriesExclude: string;
    language: string;
    captionsRequired: boolean;
    linearPlayLimits: number;
    associatedChannelId: string;
    associatedChannelId2: string;
    associatedChannelId3: string | null;
    associatedChannelId4: string | null;
    associatedChannelId5: string | null;
    requestedByRegion: string;
    serviceTier: string;
    ccService: string;
    artworkScreenshots: boolean;
    partnerCode: string;
    linearStart: string;
    linearEnd: string;
    AVODStart: string;
    AVODEnd: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    internalMediaOrderId: number;
    associatedStatus: string;
    isRemoved: boolean;
    // TODO change on real one
    workflowState: string;
}

export interface MediaOrdersItemStateType {
    rows: MediaOrdersItemType[];
    total: number;
}

export interface MediaOrdersItemPayloadType {
    payload: MediaOrdersItemStateType;
}

export interface MediaOrdersItemSortParams extends Sort, PagingParams {
    // id: string;
    seriesOrFilmTitle?: string;
    seasonNumber?: string;
    episodeNumber?: string;
    workflowState?: string;
    availsId?: string;
    dueDateToLab?: string;
    dueDateToPluto?: string;
}

export type WorkflowStateType = {[key: string]: string};

export enum WFState {
    INGEST_COMPLETED = 'INGEST_COMPLETED',
    DELIVERY_COMPLETED = 'DELIVERY_COMPLETED',
    DELIVERY_CANCELLED = 'DELIVERY_CANCELLED',
    DELIVERY_COMPLETE = 'DELIVERY_COMPLETE',
    DELIVERY_FAILED = 'DELIVERY_FAILED',
    DELIVERY_IN_PROGRESS = 'DELIVERY_IN_PROGRESS',
    DELIVERY_PENDING = 'DELIVERY_PENDING',
    INGESTING = 'INGESTING',
    INGEST_FAILED = 'INGEST_FAILED',
    INGEST_PENDING = 'INGEST_PENDING',
    INGEST_COMPLETE = 'INGEST_COMPLETE',
    IN_QC = 'IN_QC',
    QC_FAILED = 'QC_FAILED',
    QC_PASSED = 'QC_PASSED',
    READY_FOR_DELIVERY = 'READY_FOR_DELIVERY',
    READY_FOR_QC = 'READY_FOR_QC',
}

export enum MediaOrdersItemKeys {
    title = 'seriesOrFilmTitle',
    seasonNumber = 'seasonNumber',
    episodeNumber = 'episodeNumber',
    status = 'workflowState',
    availsId = 'availsId',
    dueDateToLab = 'dueDateToLab',
    dueDateToPluto = 'dueDateToPluto',
    trt = 'trt',
    clipRating = 'clipRating',
    territoriesInclude = 'territoriesInclude',
    language = 'language',
    serviceTier = 'serviceTier',
    partnerCode = 'partnerCode',
    isRemoved = 'isRemoved',
    associatedStatus = 'associatedStatus',
}
